import { Button } from "@/components/ui/button";
import { handleActionError } from "@/lib/errors";
import {
  useStockTracking,
  useUntrackStock,
  useUpsertStockTracking
} from "@/queries/customer/stock-tracking";
import { toast } from "@medusajs/ui";
import { Check, Plus } from "lucide-react";
import { memo } from "react";

interface TrackLineButtonProps {
  /** The line's variant. Null/undefined (e.g. a custom line) renders nothing. */
  variantId: string | null | undefined;
  /** Product title, for the toast + accessible label. */
  title: string;
}

/**
 * Per-line "Track" / "Tracked" toggle for order history (surface 2 of #71). This
 * is the highest-intent entry point - the salon is looking at exactly what they
 * buy, count in hand - so a tap starts tracking the variant at reorder level 1,
 * not counted yet (they set the on-hand count later in My Stock). Tracked state
 * is read from the shared `useStockTracking` cache, so it stays in sync with the
 * My Stock editor and other lines for the same variant.
 */
export const TrackLineButton = memo(function TrackLineButton({
  variantId,
  title
}: TrackLineButtonProps) {
  const { data } = useStockTracking();
  const upsert = useUpsertStockTracking();
  const untrack = useUntrackStock();

  if (!variantId) {
    return null;
  }

  const isTracked = (data?.stock_items ?? []).some(
    (item) => item.variant_id === variantId
  );

  const pending =
    (upsert.isPending && upsert.variables?.variant_id === variantId) ||
    (untrack.isPending && untrack.variables === variantId);

  const handleClick = () => {
    if (pending) {
      return;
    }
    if (isTracked) {
      untrack.mutate(variantId, {
        onSuccess: () => toast.success(`Stopped tracking ${title}`),
        onError: (err) =>
          toast.error(
            handleActionError(err, {
              boundary: "track line untrack",
              fallback:
                "We couldn't stop tracking this product. Please try again."
            })
          )
      });
    } else {
      upsert.mutate(
        { variant_id: variantId, reorder_level: 1 },
        {
          onSuccess: () => toast.success(`Now tracking ${title}`),
          onError: (err) =>
            toast.error(
              handleActionError(err, {
                boundary: "track line track",
                fallback:
                  "We couldn't start tracking this product. Please try again."
              })
            )
        }
      );
    }
  };

  return (
    <Button
      type="button"
      variant={isTracked ? "secondary" : "outline"}
      size="xs"
      onClick={handleClick}
      disabled={pending}
      aria-pressed={isTracked}
      aria-label={
        isTracked ? `Stop tracking ${title}` : `Track ${title} in My Stock`
      }
    >
      {isTracked ? (
        <>
          <Check className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Tracked
        </>
      ) : (
        <>
          <Plus className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Track
        </>
      )}
    </Button>
  );
});
